import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';
import type { Actor } from '../../lib/auth';

/**
 * NOTES — ONE COMPONENT, BOTH SIDES.
 *
 * This is the design decision, not an economy. A ticket system gives the requester a status page
 * and the agent a work item, and the two of them are never reading the same thing — which is how
 * "I told you about this in week two" becomes an argument. Here the director and the commissioner
 * render the same rows in the same order with the same names on them, because it is literally the
 * same component with two things switched: who may start a thread, and who may mark one read.
 *
 * THE COMPOSER IS AT THE TOP FOR A DIRECTOR AND ABSENT FOR HQ. The commissioner has no league and
 * nobody to write to; her way in is a reply.
 *
 * MARKING READ IS NOT A REPLY. Some notes are told, not asked — the venue moved us to the back
 * room — and needing an answer to clear one produces either a wall of "thanks!" or an inbox that
 * never empties. Replying marks it read on its own, because answering something you have not read
 * is not a thing that happens.
 */

interface Reply {
  id: string;
  authorId: string;
  authorName: string | null;
  authorRole: string | null;
  body: string;
  at: string;
}

interface NoteThread {
  id: string;
  directorId: string;
  directorName: string | null;
  leagueId: string | null;
  leagueName: string | null;
  body: string;
  status: string;
  reviewedAt: string | null;
  at: string;
  replies: Reply[];
  lastActivityAt: string;
}

interface League {
  id: string;
  name: string;
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function NotesPage({ actor }: { actor: Actor }) {
  const isCommissioner = actor.role === 'commissioner';
  const queryClient = useQueryClient();

  const [body, setBody] = useState('');
  const [leagueId, setLeagueId] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');

  const threads = useQuery({
    queryKey: ['notes'],
    queryFn: () => apiFetch<{ notes: NoteThread[] }>('/api/notes'),
  });

  /* An instructor asks about the leagues they run, which only the facilitated list returns (2026-10-17). */
  const nightsOnly = actor.role === 'instructor';
  const leagues = useQuery({
    queryKey: nightsOnly ? ['director-leagues'] : ['leagues'],
    queryFn: () => apiFetch<{ leagues: League[] }>(nightsOnly ? '/api/director/leagues' : '/api/leagues'),
    enabled: !isCommissioner,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['notes'] });

  const write = useMutation({
    mutationFn: (input: { body: string; leagueId: string | null }) =>
      apiPost<{ note: NoteThread }>('/api/notes', input),
    onSuccess: async () => {
      setBody('');
      setLeagueId('');
      await invalidate();
    },
  });

  const reply = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      apiPost<{ note: NoteThread }>(`/api/notes/${id}/replies`, { body: text }),
    onSuccess: async () => {
      setReplyTo(null);
      setReplyBody('');
      await invalidate();
      /* The dashboard shows an unread count, so it has to be re-asked or it keeps yesterday's. */
      await queryClient.invalidateQueries({ queryKey: ['hq'] });
    },
  });

  const markRead = useMutation({
    mutationFn: (id: string) => apiPost<{ note: NoteThread }>(`/api/notes/${id}/reviewed`, {}),
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ['hq'] });
    },
  });

  if (threads.isPending) return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;

  const list = threads.data?.notes ?? [];
  const error = [write.error, reply.error, markRead.error].find((e) => e instanceof ApiError) as
    | ApiError
    | undefined;

  return (
    <div className="flex w-full min-w-0 max-w-[52rem] flex-col gap-6 px-6 py-8">
      <header>
        <h1 className="font-display text-3xl leading-tight">
          {isCommissioner ? 'Notes from directors' : 'Questions & Support'}
        </h1>
        <p className="mt-1 text-[14px] text-ink-2">
          {isCommissioner
            ? 'Everything directors have told you, newest conversation first.'
            : 'Have a question about running your league? Send us a note here. We’ll respond directly to you.'}
        </p>
      </header>

      {error ? <ErrorNote>{error.message}</ErrorNote> : null}

      {!isCommissioner ? (
        <section className="rounded-md border border-light bg-paper px-5 py-4">
          <label className="block">
            <span className="text-[13px] font-medium">Your question</span>
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={3}
              placeholder="I have a question about running a league..."
              className="mt-2 w-full rounded-md border border-stroke bg-chalk px-3 py-2 text-[14px] leading-relaxed"
            />
          </label>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex min-w-0 max-w-full flex-col gap-1 text-[13px]">
              Which league is this about?
            <select
              value={leagueId}
              onChange={(event) => setLeagueId(event.target.value)}
              aria-label="Which league is this about?"
              className="h-11 w-full min-w-0 max-w-full rounded-md border border-control-border bg-chalk px-3 text-[13px]"
            >
              <option value="">Not about a particular league</option>
              {(leagues.data?.leagues ?? []).map((league) => (
                <option key={league.id} value={league.id}>
                  {league.name}
                </option>
              ))}
            </select>
            </label>
            <Button
              disabled={write.isPending || body.trim() === ''}
              onClick={() => write.mutate({ body, leagueId: leagueId || null })}
            >
              {write.isPending ? 'Sending…' : 'Send question'}
            </Button>
          </div>
        </section>
      ) : null}

      {list.length === 0 ? (
        isCommissioner ? <p className="rounded-md border border-dashed border-light px-5 py-10 text-center text-[14px] text-ink-3">Nothing from anybody. Directors write here when something needs saying.</p> : null
      ) : (
        <ol className="flex flex-col gap-4">
          {list.map((thread) => {
            const unread = thread.status === 'unread';

            return (
              <li
                key={thread.id}
                className={[
                  'rounded-md border bg-paper',
                  /* The dot is on the thread, not on individual messages. What is unread is the
                     conversation — that is the unit somebody acts on. */
                  unread && isCommissioner ? 'border-signal' : 'border-light',
                ].join(' ')}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-light px-5 py-3">
                  <div className="min-w-0">
                    <span className="text-[14px] font-medium">
                      {thread.directorName ?? 'A director'}
                    </span>
                    {thread.leagueName ? (
                      <span className="ml-2 text-[13px] text-ink-2">{thread.leagueName}</span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-3">
                    {unread && isCommissioner ? (
                      <span className="flex items-center gap-1.5 text-[12px] text-signal-d">
                        <span aria-hidden="true" className="size-2 rounded-full bg-signal" />
                        Unread
                      </span>
                    ) : null}
                    <span className="text-[12px] text-ink-3">{when(thread.at)}</span>
                  </div>
                </div>

                <div className="flex flex-col divide-y divide-light">
                  <Message
                    who={thread.directorName ?? 'A director'}
                    body={thread.body}
                    at={thread.at}
                    fromHq={false}
                  />
                  {thread.replies.map((r) => (
                    <Message
                      key={r.id}
                      who={r.authorName ?? 'Somebody'}
                      body={r.body}
                      at={r.at}
                      fromHq={r.authorRole === 'commissioner'}
                    />
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-3 border-t border-light px-5 py-3">
                  {replyTo === thread.id ? (
                    <div className="flex w-full flex-col gap-2">
                      <textarea
                        value={replyBody}
                        onChange={(event) => setReplyBody(event.target.value)}
                        rows={2}
                        aria-label="Your reply"
                        className="w-full rounded-md border border-stroke bg-chalk px-3 py-2 text-[14px] leading-relaxed"
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          size="inline"
                          disabled={reply.isPending || replyBody.trim() === ''}
                          onClick={() => reply.mutate({ id: thread.id, text: replyBody })}
                        >
                          Reply
                        </Button>
                        <Button
                          variant="secondary"
                          size="inline"
                          onClick={() => {
                            setReplyTo(null);
                            setReplyBody('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Button
                        variant="secondary"
                        size="inline"
                        onClick={() => {
                          setReplyTo(thread.id);
                          setReplyBody('');
                        }}
                      >
                        Reply
                      </Button>
                      {isCommissioner && unread ? (
                        <Button
                          variant="secondary"
                          size="inline"
                          disabled={markRead.isPending}
                          onClick={() => markRead.mutate(thread.id)}
                        >
                          Just mark it read
                        </Button>
                      ) : null}
                      {!unread && thread.reviewedAt ? (
                        <span className="text-[12px] text-ink-3">
                          {isCommissioner ? 'You read this' : 'We’ve read this'}{' '}
                          {when(thread.reviewedAt)}
                        </span>
                      ) : null}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/**
 * One thing somebody said.
 *
 * HQ's messages sit on a tinted ground so a thread is scannable at a glance, but they are the same
 * shape and the same size — nobody's contribution is styled as more official than anybody else's.
 */
function Message({
  who,
  body,
  at,
  fromHq,
}: {
  who: string;
  body: string;
  at: string;
  fromHq: boolean;
}) {
  return (
    /*
      THE SMALL TEXT STEPS UP ON THE TINTED ROW. `ink-subtle` is 4.45:1 on `tint-neutral` — it
      clears the bar on the page, on a card and on an inset panel, and misses on this fourth
      ground by five hundredths. Caught by an axe pass; see the recorded pairing in the contrast
      test, which exists so nobody puts it back.
    */
    <div className={['px-5 py-3.5', fromHq ? 'bg-tint-deep' : ''].join(' ')}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[13px] font-medium">
          {who}
          {fromHq ? (
            <span className={['ml-1.5 text-[12px]', fromHq ? 'text-ink-2' : 'text-ink-3'].join(' ')}>
              Support
            </span>
          ) : null}
        </span>
        <span className={['text-[12px]', fromHq ? 'text-ink-2' : 'text-ink-3'].join(' ')}>
          {when(at)}
        </span>
      </div>
      <p className="mt-1 whitespace-pre-wrap text-[14px] leading-relaxed">{body}</p>
    </div>
  );
}
